import type { HostProfile } from '../types';

/**
 * How complete a host's profile is, in three honest tiers.
 *
 * The old check (`headline + city + bio`, in Dashboard.tsx) was both wrong and
 * misleading: it ignored the things that actually decide whether a host gets
 * work — complete billing details and an active profile are hard filters
 * in alma-finalize's candidate query, so missing either means *never being
 * proposed at all*, no matter how good the bio is.
 *
 * Separating the tiers matters because the fields do genuinely different jobs:
 *   blocking — you are not in the candidate pool
 *   ranking  — Alma reads these when choosing between hosts
 *   winning  — the traveler sees these when choosing between the hosts Alma picked
 *
 * Video and moodboard deliberately sit in `winning`: they are never sent to the
 * ranker, so telling a host they improve matching would be untrue.
 */
export type StrengthTier = 'blocking' | 'ranking' | 'winning';

export interface StrengthItem {
  key: string;
  /** Imperative, host-facing. */
  label: string;
  done: boolean;
}

export interface StrengthTierResult {
  tier: StrengthTier;
  items: StrengthItem[];
  done: number;
  total: number;
  complete: boolean;
}

export interface HostProfileStrength {
  blocking: StrengthTierResult;
  ranking: StrengthTierResult;
  winning: StrengthTierResult;
  /** False when anything in `blocking` is missing — the host is not matchable. */
  matchable: boolean;
  /** Blocking items still outstanding, for the "why you're not live" list. */
  blockers: StrengthItem[];
  /** 0–100 across every tier, for a single at-a-glance figure. */
  percent: number;
}

const filled = (v: string | null | undefined) => !!v && v.trim().length > 0;

function summarise(tier: StrengthTier, items: StrengthItem[]): StrengthTierResult {
  const done = items.filter((i) => i.done).length;
  return { tier, items, done, total: items.length, complete: done === items.length };
}

export function hostProfileStrength(
  profile: HostProfile | null,
  billingReady: boolean,
  cityCount: number,
  avatarUrl?: string | null,
  /**
   * Whether the payout gate is currently enforced
   * (`platform_settings.require_host_payout_ready`). When it's off, matching
   * ignores payout readiness, so telling a host they're "not being matched"
   * would be false.
   */
  payoutGateEnforced = true,
): HostProfileStrength {
  const blocking = summarise('blocking', [
    {
      key: 'payouts',
      label: 'Add your billing details so you can be paid',
      // Mirrors alma-finalize's payout filter — without this the host is
      // removed from the candidate list entirely.
      done: !payoutGateEnforced || billingReady,
    },
    { key: 'city', label: 'Choose at least one city you host in', done: cityCount > 0 },
    { key: 'active', label: 'Have your profile switched on', done: profile?.active !== false },
  ]);

  const ranking = summarise('ranking', [
    { key: 'bio', label: 'Write your bio', done: filled(profile?.bio) },
    { key: 'taste_notes', label: 'Describe the travelers you suit best', done: filled(profile?.taste_notes) },
    { key: 'specialties', label: 'Pick at least three specialties', done: (profile?.specialties?.length ?? 0) >= 3 },
    { key: 'headline', label: 'Add a headline', done: filled(profile?.headline) },
  ]);

  const winning = summarise('winning', [
    { key: 'avatar', label: 'Add a profile photo', done: filled(avatarUrl) },
    { key: 'moodboard', label: 'Add at least three photos of your city', done: (profile?.moodboard_images?.length ?? 0) >= 3 },
    { key: 'video', label: 'Record a short intro video', done: filled(profile?.video_url) },
  ]);

  const all = [...blocking.items, ...ranking.items, ...winning.items];
  const percent = all.length === 0 ? 0 : Math.round((all.filter((i) => i.done).length / all.length) * 100);

  return {
    blocking,
    ranking,
    winning,
    matchable: blocking.complete,
    blockers: blocking.items.filter((i) => !i.done),
    percent,
  };
}

/** One-line summary for the dashboard nudge. */
export function strengthHeadline(s: HostProfileStrength): string {
  if (!s.matchable) return "You're not being matched yet";
  if (!s.ranking.complete) return 'Your profile is live — a few things would help Alma match you';
  if (!s.winning.complete) return 'Strong profile — a couple of things left to stand out';
  return 'Your profile is complete';
}
